import { wsClosed, wsError, receiveServerPing, receiveServerPong } from '../interface/actions'
import { eventChannel, END, buffers } from 'redux-saga'
import { take, put, call } from 'redux-saga/effects'

import { toQueryString } from '../../common/utils'

// Other plugins.
import { websocketNotification } from '../../notifications/interface/actions'
import { logManager } from '../../logs'

const INITIAL_BUFFER_SIZE = 50
const log = logManager.getLogger('CONNECTIVITY')

/**
 * Create a new websocket.
 * @method openWebsocket
 * @param  {Object} options Websocket configuration options.
 * @param {string} options.protocol Websocket protocol to use.
 * @param {string} options.server Websocket hostname.
 * @param {string} options.port Websocket port to use.
 * @param {string} options.url Websocket notification channel.
 * @param {Object} options.params A list of URL params to attach to the websocket.
 * @return {WebSocket} ws Newly connected websocket.
 */
export function openWebsocket (options) {
  // Create the websocket.
  const wsUrl = `${options.protocol}://${options.server}:${options.port}${options.url}` + toQueryString(options.params)
  log.debug(`Opening socket at: ${wsUrl}`)
  const ws = new WebSocket(wsUrl)

  // Use a promise to wait for the first message from the websocket.
  // This indicates whether the WS opened successfully or not.
  const validateWS = new Promise((resolve, reject) => {
    const onOpen = function () {
      ws.onopen = null
      ws.onerror = null
      resolve(ws)
    }
    const onError = function () {
      ws.onopen = null
      ws.onerror = null

      // TODO: Fix this?
      /* eslint-disable-next-line prefer-promise-reject-errors */
      reject({
        // Websocket doesn't return error codes for security reasons and as such all websocket
        // errors have to be handled the same.
        error: true,
        message: 'Could not connect to websocket. Received error on open.'
      })
    }
    ws.onopen = onOpen
    ws.onerror = onError
  })
  return validateWS
}

/**
 * Clean-up a provided websocket.
 * @method cleanupWebSocket
 * @param  {Websocket} ws Websocket to be cleaned-up.
 * @return {Websocket} ws The websocket after being cleaned.
 */
export function cleanupWebSocket (ws) {
  if (ws.close) {
    ws.close()
  }
  ws.onclose = null
  ws.onmessage = null
  ws.onopen = null
  ws.onerror = null
}

/**
 * Create an event channel for a given websocket
 * @param  {WebSocket} ws       The websocket to make an event channel for.
 * @param {string} [platform=link]    The backend platform associated with the websocket.
 * @return {EventChannel}              The event channel corresponding to the WebSocket
 */
function createWsChannel (ws, platform) {
  // this function handles server messages
  return eventChannel(emit => {
    // Define handlers
    ws.onmessage = message => {
      // Mark this websocket are being connected as of now.
      ws.kandy.lastContact = Date.now()

      var data = JSON.parse(message.data)
      log.debug('Received message on websocket.', data)
      if (data.connCheck) {
        // Handle CPaaS server pings
        emit(receiveServerPing(data, platform))
      } else if (data.connAck) {
        // Handle CPaaS server pongs
        emit(receiveServerPong(data, platform))
      } else {
        emit(websocketNotification(data, platform))
      }
    }
    ws.onclose = data => {
      emit(wsClosed(data, platform))
      emit(END)
    }
    ws.onerror = err => {
      emit(wsError(new Error(err), platform))
      emit(END)
    }

    return () => cleanupWebSocket(ws)
  }, buffers.expanding(INITIAL_BUFFER_SIZE))
}

/**
 * Saga worker for creating a websocket and emitting its events
 * @param {Object} ws configuration options.
 * @param {string} [platform=link]    The backend platform associated with the websocket.
 * @return {Generator}
 */
export function * wsEmitter (ws, platform) {
  var wsChannel = yield call(createWsChannel, ws, platform)
  while (true) {
    const action = yield take(wsChannel)
    yield put(action)
  }
}
